import { useState, useEffect } from 'react'

interface CountdownResult {
  timeLeft: string
  isExpired: boolean
  hours: number
  minutes: number
  seconds: number
  shouldShow: boolean // Only show if within 24 hours
}

export const useCountdown = (endAt: Date | undefined): CountdownResult => {
  const [timeLeft, setTimeLeft] = useState('00:00:00')
  const [isExpired, setIsExpired] = useState(false)
  const [hours, setHours] = useState(0)
  const [minutes, setMinutes] = useState(0)
  const [seconds, setSeconds] = useState(0)
  const [shouldShow, setShouldShow] = useState(false)

  useEffect(() => {
    if (!endAt) {
      setTimeLeft('00:00:00')
      setIsExpired(false)
      setShouldShow(false)
      setHours(0)
      setMinutes(0)
      setSeconds(0)
      return
    }

    const updateCountdown = () => {
      const now = new Date()
      const endDate = new Date(endAt)
      const diffMs = endDate.getTime() - now.getTime()
      
      if (diffMs <= 0) {
        // Coupon has expired - show expired message
        setTimeLeft('00:00:00')
        setIsExpired(true)
        setHours(0)
        setMinutes(0)
        setSeconds(0)
        setShouldShow(true) // Show expired state
        return
      }

      // Only show countdown if within 24 hours (86400000 ms = 24 hours)
      const hoursUntilEnd = diffMs / (1000 * 60 * 60)
      setShouldShow(hoursUntilEnd <= 24 && hoursUntilEnd > 0)

      const totalSeconds = Math.floor(diffMs / 1000)
      const h = Math.floor(totalSeconds / 3600)
      const m = Math.floor((totalSeconds % 3600) / 60)
      const s = totalSeconds % 60

      setHours(h)
      setMinutes(m)
      setSeconds(s)
      setTimeLeft(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`)
      setIsExpired(false)
    }

    // Initial update
    updateCountdown()

    // Set up interval to update every second
    const interval = setInterval(updateCountdown, 1000)

    return () => clearInterval(interval)
  }, [endAt])

  return { timeLeft, isExpired, hours, minutes, seconds, shouldShow }
}
